import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Calendar, User, LogOut, Clock, Loader2, AlertCircle, XCircle, Star, MessageSquare, Eye, CreditCard, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../AuthContext';
import { supabase } from '../supabaseClient';
import { reviewsApi, paymentsApi } from '../services/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { user, isAuthenticated, signOut, session } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  
  // Review state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [bookingToReview, setBookingToReview] = useState(null);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [reviewedBookings, setReviewedBookings] = useState(new Set());

  // Booking detail state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Payment state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [bookingToPay, setBookingToPay] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('web');
  const [mobilePaymentPhone, setMobilePaymentPhone] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    fetchBookings();
    fetchExistingReviews();
  }, [isAuthenticated, navigate, session]);

  // Auto-open review dialog when arriving from ReviewsPage with ?action=review
  useEffect(() => {
    if (searchParams.get('action') !== 'review' || loading || bookings.length === 0) return;
    const reviewable = bookings.find(b => canReviewBooking(b));
    if (reviewable) {
      handleReviewClick(reviewable);
    } else {
      toast({
        title: 'No Reviewable Bookings',
        description: 'You can write a review once your checkout date has passed.',
      });
    }
    // Clear the query param so it doesn't re-trigger
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading, bookings]);

  const fetchExistingReviews = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) return;

      const myReviews = await reviewsApi.getMyReviews(token);
      if (myReviews && myReviews.length > 0) {
        const reviewedIds = new Set(myReviews.map(r => r.booking_id));
        setReviewedBookings(reviewedIds);
      }
    } catch (err) {
      // Non-critical — just means we can't pre-hide the button
      console.error('Failed to fetch existing reviews:', err);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get current session token
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      if (!token) {
        // No token, show empty bookings
        setBookings([]);
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/bookings/my-bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      } else if (response.status === 401) {
        // Token might be expired
        setError('Session expired. Please log in again.');
      } else {
        // Load from localStorage as fallback (for demo/mock data)
        const storedBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
        setBookings(storedBookings);
      }
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      // Fallback to localStorage
      const storedBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      setBookings(storedBookings);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const { error } = await signOut();
    
    if (error) {
      toast({
        title: "Logout Failed",
        description: error.message,
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
    });
    
    setTimeout(() => {
      navigate('/');
    }, 1000);
  };

  const getCurrencySymbol = (curr) => {
    const symbols = { USD: '$', ZAR: 'R', GBP: '£', EUR: '€' };
    return symbols[curr] || '$';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'var(--status-success)';
      case 'pending':
        return 'var(--olive-primary)';
      case 'cancelled':
        return '#dc2626';
      default:
        return 'var(--text-secondary)';
    }
  };

  const canCancelBooking = (booking) => {
    const status = booking.status?.toLowerCase();
    // Can only cancel pending or confirmed bookings
    return status === 'pending' || status === 'confirmed';
  };

  const handleCancelClick = (booking) => {
    setBookingToCancel(booking);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!bookingToCancel) return;

    try {
      setCancelling(true);
      
      // Get current session token
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        toast({
          title: "Authentication Required",
          description: "Please log in again to cancel your booking.",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch(`${API_URL}/api/bookings/${bookingToCancel.id}/cancel`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: "Booking Cancelled",
          description: `Your booking ${bookingToCancel.booking_reference || bookingToCancel.bookingReference} has been cancelled successfully.`,
        });
        
        // Update local state
        setBookings(prev => prev.map(b => 
          (b.id === bookingToCancel.id || b.booking_reference === bookingToCancel.booking_reference)
            ? { ...b, status: 'cancelled' }
            : b
        ));
        
        // Also update localStorage if using fallback
        const storedBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
        const updatedStored = storedBookings.map(b => 
          (b.bookingReference === (bookingToCancel.booking_reference || bookingToCancel.bookingReference))
            ? { ...b, status: 'cancelled' }
            : b
        );
        localStorage.setItem('bookings', JSON.stringify(updatedStored));
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to cancel booking' }));
        toast({
          title: "Cancellation Failed",
          description: errorData.detail || "Unable to cancel booking. Please try again.",
          variant: "destructive"
        });
      }
    } catch (err) {
      console.error('Failed to cancel booking:', err);
      toast({
        title: "Cancellation Failed",
        description: "An error occurred. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setCancelling(false);
      setCancelDialogOpen(false);
      setBookingToCancel(null);
    }
  };

  // Review functions
  const canReviewBooking = (booking) => {
    const status = booking.status?.toLowerCase();
    const bookingId = booking.id || booking.booking_reference || booking.bookingReference;
    if (reviewedBookings.has(bookingId)) return false;
    // Only confirmed or completed bookings can be reviewed
    if (status !== 'confirmed' && status !== 'completed') return false;
    // Checkout date must have passed
    const checkoutDate = new Date(booking.check_out || booking.checkOut);
    return checkoutDate < new Date();
  };

  const canPayBooking = (booking) => {
    const paymentStatus = booking.payment_status?.toLowerCase();
    const bookingStatus = booking.status?.toLowerCase();
    // Can pay if not already paid and booking isn't cancelled
    return paymentStatus !== 'paid' && bookingStatus !== 'cancelled';
  };

  const handlePayClick = (booking) => {
    setBookingToPay(booking);
    setPaymentMethod('web');
    setMobilePaymentPhone('');
    setPaymentDialogOpen(true);
  };

  const handlePayment = async () => {
    setProcessingPayment(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        toast({
          title: "Session Expired",
          description: "Please sign in again to make a payment.",
          variant: "destructive"
        });
        setProcessingPayment(false);
        return;
      }

      if (paymentMethod === 'ecocash' && !mobilePaymentPhone) {
        toast({
          title: "Phone Number Required",
          description: "Please enter your EcoCash phone number.",
          variant: "destructive"
        });
        setProcessingPayment(false);
        return;
      }

      const response = await paymentsApi.initiate(
        bookingToPay.id,
        paymentMethod,
        mobilePaymentPhone || null,
        token
      );

      if (response.success) {
        if (paymentMethod === 'web' && response.payment_url) {
          toast({
            title: "Redirecting to Payment",
            description: "You will be redirected to Paynow to complete your payment.",
          });
          setTimeout(() => {
            window.location.href = response.payment_url;
          }, 1000);
        } else {
          toast({
            title: "Payment Initiated",
            description: response.instructions || "Please check your phone to complete the payment.",
          });
          setPaymentDialogOpen(false);
          setTimeout(() => {
            navigate(`/payment-status?ref=${bookingToPay.booking_reference || bookingToPay.bookingReference}`);
          }, 1500);
        }
      } else {
        toast({
          title: "Payment Failed",
          description: response.error || "Could not initiate payment. Please try again.",
          variant: "destructive"
        });
      }
    } catch (err) {
      console.error('Payment initiation failed:', err);
      toast({
        title: "Payment Error",
        description: err.message || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleReviewClick = (booking) => {
    setBookingToReview(booking);
    setReviewRating(5);
    setReviewText('');
    setReviewDialogOpen(true);
  };

  const handleReviewSubmit = async () => {
    if (!bookingToReview || reviewText.length < 10) {
      toast({
        title: "Review Too Short",
        description: "Please write at least 10 characters in your review.",
        variant: "destructive"
      });
      return;
    }

    try {
      setSubmittingReview(true);
      
      // Get current session token
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        toast({
          title: "Authentication Required",
          description: "Please log in again to submit your review.",
          variant: "destructive"
        });
        return;
      }

      const bookingId = bookingToReview.id;
      const params = new URLSearchParams({
        booking_id: bookingId,
        rating: reviewRating.toString(),
        review_text: reviewText
      });

      const response = await fetch(`${API_URL}/api/reviews/?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: "Review Submitted!",
          description: "Thank you for your feedback. Your review will be visible after admin approval.",
        });
        
        // Mark this booking as reviewed
        const bookingIdKey = bookingToReview.id || bookingToReview.booking_reference || bookingToReview.bookingReference;
        setReviewedBookings(prev => new Set([...prev, bookingIdKey]));
        
        setReviewDialogOpen(false);
        setBookingToReview(null);
        setReviewText('');
        setReviewRating(5);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to submit review' }));
        toast({
          title: "Submission Failed",
          description: errorData.detail || "Unable to submit review. Please try again.",
          variant: "destructive"
        });
      }
    } catch (err) {
      console.error('Failed to submit review:', err);
      toast({
        title: "Submission Failed",
        description: "An error occurred. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setSubmittingReview(false);
    }
  };

  // Star rating component
  const StarRating = ({ rating, onRatingChange, readonly = false }) => {
    const [hoverRating, setHoverRating] = useState(0);
    
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={readonly}
            onClick={() => !readonly && onRatingChange(star)}
            onMouseEnter={() => !readonly && setHoverRating(star)}
            onMouseLeave={() => !readonly && setHoverRating(0)}
            className={`transition-transform ${!readonly && 'hover:scale-110 cursor-pointer'}`}
            style={{ background: 'none', border: 'none', padding: '2px' }}
          >
            <Star
              size={28}
              fill={(hoverRating || rating) >= star ? 'var(--olive-primary)' : 'none'}
              style={{ 
                color: (hoverRating || rating) >= star ? 'var(--olive-primary)' : 'var(--border-light)',
                transition: 'all 0.2s'
              }}
            />
          </button>
        ))}
      </div>
    );
  };

  if (!isAuthenticated) {
    return null; // Will redirect
  }

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }} data-testid="dashboard-page">
      <section className="section-padding">
        <div className="container max-w-6xl">
          {/* Header */}
          <div className="flex justify-between items-center mb-12">
            <div>
              <h1 className="hero-medium mb-2">My Dashboard</h1>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {user?.email || 'Guest'}
              </p>
              {user?.user_metadata?.full_name && (
                <p className="body-small" style={{ color: 'var(--text-light)' }}>
                  Welcome back, {user.user_metadata.full_name}
                </p>
              )}
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2"
              style={{ borderRadius: 0 }}
              data-testid="logout-btn"
            >
              <LogOut size={16} />
              Logout
            </Button>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <Link to="/booking" data-testid="new-booking-link">
              <div
                className="p-6 text-center hover-lift cursor-pointer"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-light)'
                }}
              >
                <Calendar size={32} className="mx-auto mb-4" style={{ color: 'var(--text-primary)' }} />
                <h3 className="heading-3 mb-2">New Booking</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Book your next stay
                </p>
              </div>
            </Link>
            
            <Link to="/profile" data-testid="profile-link">
              <div
                className="p-6 text-center hover-lift cursor-pointer"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-light)'
                }}
              >
                <User size={32} className="mx-auto mb-4" style={{ color: 'var(--text-primary)' }} />
                <h3 className="heading-3 mb-2">My Profile</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Edit your details
                </p>
              </div>
            </Link>
            
            <Link to="/rooms" data-testid="explore-rooms-link">
              <div
                className="p-6 text-center hover-lift cursor-pointer"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-light)'
                }}
              >
                <Calendar size={32} className="mx-auto mb-4" style={{ color: 'var(--text-primary)' }} />
                <h3 className="heading-3 mb-2">Explore Rooms</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  View available rooms
                </p>
              </div>
            </Link>
            
            <div
              className="p-6 text-center"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-light)'
              }}
            >
              <Clock size={32} className="mx-auto mb-4" style={{ color: 'var(--text-primary)' }} />
              <h3 className="heading-3 mb-2" data-testid="bookings-count">{bookings.length}</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Total Bookings
              </p>
            </div>
          </div>

          {/* Bookings List */}
          <div>
            <h2 className="heading-2 mb-6">My Bookings</h2>
            
            {error && (
              <div className="mb-6 p-4 flex items-center gap-3" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
                <AlertCircle size={20} style={{ color: 'var(--text-secondary)' }} />
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{error}</p>
              </div>
            )}
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin" size={32} style={{ color: 'var(--olive-primary)' }} />
              </div>
            ) : bookings.length === 0 ? (
              <div
                className="p-12 text-center"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-light)'
                }}
                data-testid="no-bookings-message"
              >
                <p className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>
                  You don't have any bookings yet.
                </p>
                <Link to="/booking">
                  <Button className="btn-primary" data-testid="make-booking-btn">Make a Booking</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-6" data-testid="bookings-list">
                {bookings.map((booking) => (
                  <div
                    key={booking.booking_reference || booking.bookingReference}
                    className="p-8 hover-lift cursor-pointer"
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-light)'
                    }}
                    onClick={() => {
                      setSelectedBooking(booking);
                      setDetailDialogOpen(true);
                    }}
                    data-testid={`booking-item-${booking.booking_reference || booking.bookingReference}`}
                  >
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h3 className="heading-3 mb-2">{booking.room_name || booking.roomName}</h3>
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          Booking Reference: {booking.booking_reference || booking.bookingReference}
                        </p>
                      </div>
                      <span
                        className="px-4 py-2 text-sm uppercase"
                        style={{
                          backgroundColor: getStatusColor(booking.status),
                          color: 'var(--bg-primary)'
                        }}
                      >
                        {booking.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      <div>
                        <p className="body-small mb-1" style={{ color: 'var(--text-secondary)' }}>
                          Check-In
                        </p>
                        <p className="body-regular">
                          {new Date(booking.check_in || booking.checkIn).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="body-small mb-1" style={{ color: 'var(--text-secondary)' }}>
                          Check-Out
                        </p>
                        <p className="body-regular">
                          {new Date(booking.check_out || booking.checkOut).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="body-small mb-1" style={{ color: 'var(--text-secondary)' }}>
                          Total Amount
                        </p>
                        <p className="heading-3">
                          {getCurrencySymbol(booking.currency)}{(booking.total || 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="flex items-end justify-end gap-2 flex-wrap">
                        {canPayBooking(booking) && (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePayClick(booking);
                            }}
                            className="flex items-center gap-2"
                            style={{
                              borderRadius: 0,
                              backgroundColor: 'var(--olive-primary)',
                              color: 'white'
                            }}
                            data-testid={`pay-booking-${booking.booking_reference || booking.bookingReference}`}
                          >
                            <CreditCard size={16} />
                            Pay Now
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBooking(booking);
                            setDetailDialogOpen(true);
                          }}
                          className="flex items-center gap-2"
                          style={{
                            borderRadius: 0,
                            borderColor: 'var(--olive-primary)',
                            color: 'var(--olive-primary)'
                          }}
                          data-testid={`view-booking-${booking.booking_reference || booking.bookingReference}`}
                        >
                          <Eye size={16} />
                          View Details
                        </Button>
                        {canReviewBooking(booking) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleReviewClick(booking); }}
                            className="flex items-center gap-2"
                            style={{ 
                              borderRadius: 0,
                              borderColor: 'var(--olive-primary)',
                              color: 'var(--olive-primary)'
                            }}
                            data-testid={`review-booking-${booking.booking_reference || booking.bookingReference}`}
                          >
                            <MessageSquare size={16} />
                            Write Review
                          </Button>
                        )}
                        {/* Cancel Booking - disabled for now, to be re-enabled later
                        {canCancelBooking(booking) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleCancelClick(booking); }}
                            className="flex items-center gap-2"
                            style={{ 
                              borderRadius: 0,
                              borderColor: '#dc2626',
                              color: '#dc2626'
                            }}
                            data-testid={`cancel-booking-${booking.booking_reference || booking.bookingReference}`}
                          >
                            <XCircle size={16} />
                            Cancel Booking
                          </Button>
                        )}
                        */}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Booking Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-lg" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-light)', maxHeight: '85vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle className="heading-3" style={{ color: 'var(--text-primary)' }}>
              Booking Details
            </DialogTitle>
            <DialogDescription className="body-small" style={{ color: 'var(--text-secondary)' }}>
              Reference: {selectedBooking?.booking_reference || selectedBooking?.bookingReference}
            </DialogDescription>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-5 py-2">
              {/* Status & Room */}
              <div className="flex items-center justify-between">
                <h4 className="heading-4" style={{ color: 'var(--text-primary)' }}>
                  {selectedBooking.room_name || selectedBooking.roomName}
                </h4>
                <span
                  className="body-small px-3 py-1"
                  style={{
                    backgroundColor: selectedBooking.status === 'confirmed'
                      ? 'rgba(95, 111, 82, 0.15)'
                      : selectedBooking.status === 'cancelled'
                      ? 'rgba(220, 38, 38, 0.1)'
                      : 'rgba(234, 179, 8, 0.15)',
                    color: selectedBooking.status === 'confirmed'
                      ? 'var(--olive-primary)'
                      : selectedBooking.status === 'cancelled'
                      ? '#dc2626'
                      : '#a16207',
                    textTransform: 'capitalize'
                  }}
                >
                  {selectedBooking.status}
                </span>
              </div>

              <hr style={{ borderColor: 'var(--border-light)' }} />

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="body-small" style={{ color: 'var(--text-muted)' }}>Check-in</p>
                  <p className="body-regular font-medium" style={{ color: 'var(--text-primary)' }}>
                    {new Date(selectedBooking.check_in || selectedBooking.checkIn).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                  </p>
                </div>
                <div>
                  <p className="body-small" style={{ color: 'var(--text-muted)' }}>Check-out</p>
                  <p className="body-regular font-medium" style={{ color: 'var(--text-primary)' }}>
                    {new Date(selectedBooking.check_out || selectedBooking.checkOut).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                  </p>
                </div>
              </div>

              {/* Guest Count */}
              <div>
                <p className="body-small" style={{ color: 'var(--text-muted)' }}>Guests</p>
                <p className="body-regular" style={{ color: 'var(--text-primary)' }}>
                  {selectedBooking.guests || selectedBooking.adults || 1} guest{(selectedBooking.guests || selectedBooking.adults || 1) > 1 ? 's' : ''}
                  {selectedBooking.children > 0 && `, ${selectedBooking.children} child${selectedBooking.children > 1 ? 'ren' : ''}`}
                </p>
              </div>

              <hr style={{ borderColor: 'var(--border-light)' }} />

              {/* Guest Info */}
              <div>
                <p className="body-small font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Guest Information</p>
                <div className="space-y-1">
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {selectedBooking.guest_first_name || selectedBooking.guestFirstName}{' '}
                    {selectedBooking.guest_last_name || selectedBooking.guestLastName}
                  </p>
                  {(selectedBooking.guest_email || selectedBooking.guestEmail) && (
                    <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                      {selectedBooking.guest_email || selectedBooking.guestEmail}
                    </p>
                  )}
                  {(selectedBooking.guest_phone || selectedBooking.guestPhone) && (
                    <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                      {selectedBooking.guest_phone || selectedBooking.guestPhone}
                    </p>
                  )}
                </div>
              </div>

              {/* Special Requests */}
              {(selectedBooking.special_requests || selectedBooking.specialRequests) && (
                <>
                  <hr style={{ borderColor: 'var(--border-light)' }} />
                  <div>
                    <p className="body-small font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Special Requests</p>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {selectedBooking.special_requests || selectedBooking.specialRequests}
                    </p>
                  </div>
                </>
              )}

              <hr style={{ borderColor: 'var(--border-light)' }} />

              {/* Pricing */}
              <div>
                <p className="body-small font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Payment Summary</p>
                <div className="space-y-1">
                  {(selectedBooking.subtotal || selectedBooking.billing_subtotal) && (
                    <div className="flex justify-between">
                      <span className="body-small" style={{ color: 'var(--text-muted)' }}>Subtotal</span>
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        ${parseFloat(selectedBooking.subtotal || selectedBooking.billing_subtotal || 0).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {(selectedBooking.tax || selectedBooking.billing_tax) && (
                    <div className="flex justify-between">
                      <span className="body-small" style={{ color: 'var(--text-muted)' }}>Tax</span>
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        ${parseFloat(selectedBooking.tax || selectedBooking.billing_tax || 0).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-1" style={{ borderTop: '1px solid var(--border-light)' }}>
                    <span className="body-regular font-semibold" style={{ color: 'var(--text-primary)' }}>Total</span>
                    <span className="body-regular font-semibold" style={{ color: 'var(--olive-primary)' }}>
                      ${parseFloat(selectedBooking.total_amount || selectedBooking.total || selectedBooking.billing_total || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Status */}
              <div className="flex justify-between items-center">
                <span className="body-small" style={{ color: 'var(--text-muted)' }}>Payment Status</span>
                <span
                  className="body-small px-3 py-1"
                  style={{
                    backgroundColor: selectedBooking.payment_status === 'paid'
                      ? 'rgba(95, 111, 82, 0.15)'
                      : 'rgba(234, 179, 8, 0.15)',
                    color: selectedBooking.payment_status === 'paid'
                      ? 'var(--olive-primary)'
                      : '#a16207',
                    textTransform: 'capitalize'
                  }}
                >
                  {selectedBooking.payment_status || 'Pending'}
                </span>
              </div>

              {/* Pay Now Button in Detail Dialog */}
              {canPayBooking(selectedBooking) && (
                <Button
                  onClick={() => {
                    setDetailDialogOpen(false);
                    handlePayClick(selectedBooking);
                  }}
                  className="w-full flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: 'var(--olive-primary)',
                    color: 'white',
                    borderRadius: 0
                  }}
                >
                  <CreditCard size={16} />
                  Pay Now — {getCurrencySymbol(selectedBooking.currency)}{parseFloat(selectedBooking.total || 0).toFixed(2)}
                </Button>
              )}

              {/* Booked Date */}
              <p className="body-small text-center" style={{ color: 'var(--text-muted)' }}>
                Booked on {new Date(selectedBooking.created_at || selectedBooking.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => setDetailDialogOpen(false)}
              style={{
                backgroundColor: 'var(--olive-primary)',
                color: 'white',
                borderRadius: 0
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="heading-3">Cancel Booking?</AlertDialogTitle>
            <AlertDialogDescription className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              Are you sure you want to cancel your booking for{' '}
              <strong>{bookingToCancel?.room_name || bookingToCancel?.roomName}</strong>?
              <br /><br />
              <span className="body-small">
                Booking Reference: {bookingToCancel?.booking_reference || bookingToCancel?.bookingReference}
                <br />
                Check-in: {bookingToCancel && new Date(bookingToCancel.check_in || bookingToCancel.checkIn).toLocaleDateString()}
              </span>
              <br /><br />
              This action cannot be undone. Cancellation is free up to 48 hours before check-in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              style={{ borderRadius: 0 }}
              disabled={cancelling}
            >
              Keep Booking
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelConfirm}
              disabled={cancelling}
              style={{ 
                borderRadius: 0,
                backgroundColor: '#dc2626',
                color: 'white'
              }}
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Cancelling...
                </>
              ) : (
                'Yes, Cancel Booking'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Write Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent 
          className="sm:max-w-[500px]"
          style={{ 
            backgroundColor: 'var(--bg-primary)', 
            border: '1px solid var(--border-light)',
            borderRadius: 0
          }}
        >
          <DialogHeader>
            <DialogTitle className="heading-3">Write a Review</DialogTitle>
            <DialogDescription className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              Share your experience at <strong>{bookingToReview?.room_name || bookingToReview?.roomName}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Star Rating */}
            <div className="space-y-2">
              <Label className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Your Rating
              </Label>
              <div className="flex items-center gap-4">
                <StarRating 
                  rating={reviewRating} 
                  onRatingChange={setReviewRating}
                />
                <span className="body-regular" style={{ color: 'var(--text-primary)' }}>
                  {reviewRating} / 5
                </span>
              </div>
            </div>

            {/* Review Text */}
            <div className="space-y-2">
              <Label htmlFor="reviewText" className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Your Review
              </Label>
              <Textarea
                id="reviewText"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Tell us about your stay... What did you love? Any suggestions for improvement?"
                rows={5}
                style={{ 
                  borderRadius: 0,
                  backgroundColor: 'var(--bg-secondary)',
                  borderColor: 'var(--border-light)',
                  resize: 'none'
                }}
                data-testid="review-text-input"
              />
              <p className="text-xs" style={{ color: 'var(--text-light)' }}>
                {reviewText.length} / 10 characters minimum
                {reviewText.length < 10 && reviewText.length > 0 && (
                  <span style={{ color: '#dc2626' }}> — Need {10 - reviewText.length} more</span>
                )}
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setReviewDialogOpen(false)}
              disabled={submittingReview}
              style={{ borderRadius: 0 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReviewSubmit}
              disabled={submittingReview || reviewText.length < 10}
              className="btn-primary flex items-center gap-2"
              data-testid="submit-review-btn"
            >
              {submittingReview ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Submitting...
                </>
              ) : (
                <>
                  <Star size={16} />
                  Submit Review
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Method Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={(open) => {
        if (!processingPayment) setPaymentDialogOpen(open);
      }}>
        <DialogContent className="sm:max-w-md" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
          <DialogHeader>
            <DialogTitle className="heading-3" style={{ color: 'var(--text-primary)' }}>
              Make Payment
            </DialogTitle>
            <DialogDescription className="body-small" style={{ color: 'var(--text-secondary)' }}>
              {bookingToPay && (
                <>
                  Booking: {bookingToPay.booking_reference || bookingToPay.bookingReference}
                  {' — '}
                  {getCurrencySymbol(bookingToPay.currency)}{parseFloat(bookingToPay.total || 0).toFixed(2)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <RadioGroup
              value={paymentMethod}
              onValueChange={setPaymentMethod}
              className="space-y-3"
            >
              {/* Visa / Mastercard Option */}
              <div
                className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                  paymentMethod === 'web' ? 'border-[var(--olive-primary)] bg-[var(--bg-secondary)]' : 'border-[var(--border-light)]'
                }`}
                onClick={() => setPaymentMethod('web')}
              >
                <RadioGroupItem value="web" id="dash-web" />
                <CreditCard size={24} style={{ color: 'var(--olive-primary)' }} />
                <div className="flex-1">
                  <Label htmlFor="dash-web" className="body-regular font-medium cursor-pointer">
                    Visa / Mastercard (USD)
                  </Label>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Pay securely via Paynow using Visa or Mastercard
                  </p>
                </div>
              </div>

              {/* EcoCash Option */}
              <div
                className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                  paymentMethod === 'ecocash' ? 'border-[var(--olive-primary)] bg-[var(--bg-secondary)]' : 'border-[var(--border-light)]'
                }`}
                onClick={() => setPaymentMethod('ecocash')}
              >
                <RadioGroupItem value="ecocash" id="dash-ecocash" />
                <Smartphone size={24} style={{ color: '#4CAF50' }} />
                <div className="flex-1">
                  <Label htmlFor="dash-ecocash" className="body-regular font-medium cursor-pointer">
                    EcoCash (USD)
                  </Label>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Pay using EcoCash mobile money in USD
                  </p>
                </div>
              </div>
            </RadioGroup>

            {/* Phone Number Input for EcoCash */}
            {paymentMethod === 'ecocash' && (
              <div className="mt-4">
                <Label htmlFor="dashMobilePhone" className="body-regular">
                  EcoCash Phone Number
                </Label>
                <Input
                  id="dashMobilePhone"
                  type="tel"
                  placeholder="e.g., 0771234567"
                  value={mobilePaymentPhone}
                  onChange={(e) => setMobilePaymentPhone(e.target.value)}
                  className="mt-2"
                  style={{ borderColor: 'var(--border-medium)' }}
                />
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Enter the phone number registered with EcoCash
                </p>
              </div>
            )}

            {/* Paynow Trust Badge */}
            <div className="mt-4 pt-4 flex items-center justify-center" style={{ borderTop: '1px solid var(--border-light)' }}>
              <img
                src="/assets/paynowbadge.svg"
                alt="Secured by Paynow"
                style={{ height: '100px', width: 'auto' }}
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPaymentDialogOpen(false)}
              disabled={processingPayment}
              style={{ borderRadius: 0, borderColor: 'var(--border-medium)' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              disabled={processingPayment || (paymentMethod === 'ecocash' && !mobilePaymentPhone)}
              style={{ backgroundColor: 'var(--olive-primary)', color: 'white', borderRadius: 0 }}
            >
              {processingPayment ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard size={16} className="mr-2" />
                  {bookingToPay && `Pay ${getCurrencySymbol(bookingToPay.currency)}${parseFloat(bookingToPay.total || 0).toFixed(2)}`}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
